import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { juntarNomes } from './MinhasDemandas';

// Finalizar dia com o rodízio (Adnéia, 16/10/2026): antes de confirmar, mostra o que
// estava com a pessoa e não foi finalizado. Quem fechou tudo e tem colega com demanda
// aberta recebe o recado para dar uma mãozinha (decisão 6a). O comunicado vai para a
// gestão pelo POST /api/atendimentos/finalizar-dia, como antes.

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinalizarDiaDialog = ({ open, onOpenChange }) => {
  const { getAuthHeader } = useAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  useEffect(() => {
    if (!open) return;
    let vivo = true;
    setCarregando(true);
    setDados(null);
    axios.get(`${API_URL}/api/rodizio/hoje`, { headers: getAuthHeader() })
      .then((r) => { if (vivo) setDados(r.data); })
      .catch(() => { if (vivo) setDados({ ativo: false }); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const finalizar = async () => {
    setFinalizando(true);
    try {
      const r = await axios.post(`${API_URL}/api/atendimentos/finalizar-dia`, {}, { headers: getAuthHeader() });
      if (r.data.success) {
        toast.success('Dia finalizado. Obrigada!');
        onOpenChange(false);
      }
    } catch (error) {
      toast.error('Erro ao finalizar o dia: ' + (error?.response?.data?.detail || error?.message || ''));
    } finally {
      setFinalizando(false);
    }
  };

  const comRodizio = !!(dados && dados.ativo && dados.eu);
  const abertas = comRodizio ? (dados.eu.abertas || []) : [];
  const colega = comRodizio ? dados.colega : null;
  const ajudar = comRodizio && abertas.length === 0 && colega && (colega.abertas || []).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Finalizar dia</DialogTitle>
          <DialogDescription>{comRodizio ? `Suas demandas de ${dados.data_br}` : 'Fechamento do dia'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {carregando && <p className="text-sm text-muted-foreground">Conferindo suas demandas…</p>}

          {!carregando && comRodizio && abertas.length > 0 && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <p className="font-semibold text-amber-900 dark:text-amber-100">
                {abertas.length === 1
                  ? `Atenção: ${abertas[0].tipo === 'canal' ? 'o canal' : 'a atividade'} ${abertas[0].nome} estava com você e não foi finalizad${abertas[0].tipo === 'canal' ? 'o' : 'a'}.`
                  : 'Atenção: estas demandas estavam com você e não foram finalizadas:'}
              </p>
              {abertas.length > 1 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {abertas.map((a) => (
                    <span key={a.tipo + a.nome} className="text-xs px-2 py-0.5 rounded-md bg-white/70 dark:bg-black/20 border border-amber-200 dark:border-amber-800">
                      {a.nome} <span className="opacity-60">· {a.tipo}</span>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-sm mt-2 text-amber-900 dark:text-amber-100">Finalizar mesmo assim?</p>
            </div>
          )}

          {!carregando && comRodizio && abertas.length === 0 && (
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
              <p className="font-semibold text-green-800 dark:text-green-200">Tudo certo: suas demandas de hoje estão finalizadas.</p>
            </div>
          )}

          {!carregando && ajudar && (
            <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-sm">
              {colega.primeiro_nome} ainda está com algumas demandas abertas: {juntarNomes(colega.abertas.map((a) => a.nome))}.
              {' '}Se tiver um tempinho, dá uma mãozinha? Obrigada pelo seu dia!
            </div>
          )}

          {!carregando && !comRodizio && (
            <p className="text-sm text-muted-foreground">Deseja confirmar a finalização do dia?</p>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {abertas.length > 0 ? 'Voltar e terminar' : 'Revisar'}
          </Button>
          <Button onClick={finalizar} disabled={finalizando || carregando} className="bg-green-600 hover:bg-green-700">
            {finalizando ? 'Finalizando…' : (abertas.length > 0 ? 'Finalizar mesmo assim' : 'Confirmar finalização')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FinalizarDiaDialog;
